import React, { useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Nav } from "reactstrap";
import { AddClassroomMembersType, CoreClassroomAdminClassroomListing, CoreClassroomAdminCourseState } from "../../../../models/CoreClassroomAdminClassroom";
import ModalComponent from '../../../misc/ModalComponent';
import { InfoType } from '../ClassroomViewTools';
import ClassroomViewToolAddMembers from './ClassroomViewToolAddMembers';
import ClassroomViewToolChangeStatus from './ClassroomViewToolChangeStatus';
import ClassroomViewToolDeleteClassroom from './ClassroomViewToolDeleteClassroom';
import ClassroomViewToolUpdateOwner from './ClassroomViewToolUpdateOwner';
import { isAuthorized, FlowSyncAccessPolicy } from '../../../../models/FlowSyncAccessPolicy';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from '../../../../store/store';


interface ClassroomAdminToolsOperationProps {
    classroom: CoreClassroomAdminClassroomListing
}

const ClassroomAdminToolsOperation = (props: ClassroomAdminToolsOperationProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);

    const [open, setOpen] = useState<boolean>(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [info, setInfo] = useState<InfoType>(InfoType.UNDEFINED);
    const [type, setType] = useState<AddClassroomMembersType | undefined>(undefined);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasClassroomEdit = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.CLASSROOM_ADMIN_EDIT);

    const isSynched = props.classroom.synched === true;
    const canManageUnsynched = !isSynched;

    const canChangeStatus = props.classroom.synched === false
            || (props.classroom.synched === true
                && props.classroom.state.courseState === CoreClassroomAdminCourseState[CoreClassroomAdminCourseState.Archived]);

    const hasVisibleTools = canManageUnsynched || canChangeStatus;

    const renderBody = () => {
        switch (info) {
            case InfoType.DELETE_CLASSROOM:
                return <ClassroomViewToolDeleteClassroom classroom={props.classroom} />;
            case InfoType.UPDATE_OWNER:
                return <ClassroomViewToolUpdateOwner classroom={props.classroom} />;
            case InfoType.ADD_MEMBERS:
                return <ClassroomViewToolAddMembers type={type} classroom={props.classroom} />;
            case InfoType.CHANGE_STATUS:
                return <ClassroomViewToolChangeStatus classroom={props.classroom} />;
            case InfoType.UNDEFINED: return null;
            default: return null;
        }
    }

    return (<>
        {hasClassroomEdit && hasVisibleTools && <>
            <Nav className="right">
                <Dropdown nav isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                    <DropdownToggle nav style={{ display: 'inline' }} title={t('tools.title', { ns: 'classroomAdmin' })} >
                        <FontAwesomeIcon icon="ellipsis" className="icon" />
                    </DropdownToggle>
                    <DropdownMenu>
                        {canManageUnsynched && <DropdownItem onClick={() => { setInfo(InfoType.DELETE_CLASSROOM); setOpen(true) }} >{t(`tools.${InfoType.DELETE_CLASSROOM}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {canManageUnsynched && <DropdownItem onClick={() => { setInfo(InfoType.UPDATE_OWNER); setOpen(true) }}>{t(`tools.${InfoType.UPDATE_OWNER}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {canManageUnsynched && <DropdownItem onClick={() => { setInfo(InfoType.ADD_MEMBERS); setOpen(true); setType(AddClassroomMembersType.Student) }}>{t(`tools.${InfoType.ADD_MEMBERS}.studentTitle`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {canManageUnsynched && <DropdownItem onClick={() => { setInfo(InfoType.ADD_MEMBERS); setOpen(true); setType(AddClassroomMembersType.Teacher) }}>{t(`tools.${InfoType.ADD_MEMBERS}.teacherTitle`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {canChangeStatus && <DropdownItem onClick={() => { setInfo(InfoType.CHANGE_STATUS); setOpen(true) }}>{t(`tools.${InfoType.CHANGE_STATUS}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                    </DropdownMenu>
                </Dropdown>
            </Nav>

            {open &&
                <ModalComponent
                    isOpen={open}
                    toggleModal={() => { setOpen(false); setInfo(InfoType.UNDEFINED); setType(undefined) }}
                    header={t(`tools.${info}.title`, { ns: 'classroomAdmin' })}
                    cancel={t('action.close', { ns: 'common' })}
                    size="lg"
                >
                    {renderBody()}
                </ModalComponent>}
        </>}
    </>)
}

export default ClassroomAdminToolsOperation;
